import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivationEnd, Data, NavigationEnd, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { AnalyticsService, hasOpenRegistrationEnabled, HouseholdProfilesService, OrganizationService, SiteMembershipService, UserPreferenceService, WebViewService } from '@se-po/shared-data-access-services'
import { firstValueFrom, Subscription } from 'rxjs'
import { SeFeLayoutComponent, SeFeLeftNavEvent, SeFeLeftNavRouterService } from 'se-fe-left-nav'
import { SeFeThemesLoader, setSeRem } from 'se-fe-themes'

import { environment } from '@se-po/shared-environments'

import { SeFeTranslationsLoader } from 'se-fe-translations'
import { Persona } from 'se-resource-types/dist/lib/CentralService/Profiles'
import { DibSession } from './models'
import { DibSessionService, ModalService, SpinnerService } from './services'


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('layout') private layout: SeFeLayoutComponent

  public isWebView = false
  public loaded = false
  public navItems: any[] = []
  public footerLinks: any[] = []
  public path: string
  public persona: Persona
  public personaCropImage: string
  public hideLeftNav = true

  private routeConfigData: Data | undefined
  private showOrderHistory = false
  private showOpenRegistration = false
  private subscription: Subscription = new Subscription()
  private dibs: DibSession[]
  private themeLoadPromise: Promise<void>

  constructor(
    private el: ElementRef,
    private translate: TranslateService,
    private router: Router,
    private routerService: SeFeLeftNavRouterService,
    private seFeTranslationsLoader: SeFeTranslationsLoader,
    private analyticsService: AnalyticsService,
    private titleService: Title,
    private dibSessionService: DibSessionService,
    private householdProfilesService: HouseholdProfilesService,
    private spinnerService: SpinnerService,
    private window: Window,
    private organizationService: OrganizationService,
    private siteMembershipService: SiteMembershipService,
    private userPreferenceService: UserPreferenceService,
    protected modalService: ModalService,
    private webViewService: WebViewService
  ) {
    (window as any).PO_DEPLOY_VERSION = 2
    this.resetSeRems()
    this.themeLoadPromise = SeFeThemesLoader.load({
      shadowRoot: this.el.nativeElement.shadowRoot,
      supportedThemes: 'light',
      url: environment.seFeAngularServices,
    })
  }

  public async ngOnInit(): Promise<void> {
    this.webViewService.loadCurrent()
    this.webViewService.current$.subscribe((isWebView: boolean) => {
      this.isWebView = isWebView
    })
    this.subscription.add(this.router.events.subscribe((val) => {
      if (val instanceof ActivationEnd && !val.snapshot.children.length) {
        this.routeConfigData = this.navigationContextData(val)

        this.modalService.close()
        this.showHideNav(val)

        // if the event is after initial page load, update the title and left nav
        if (this.navItems.length > 0) {
          this.setTitle(this.routeConfigData?.title)
          this.setLeftNavSelected(this.routeConfigData)
        }
      }
      if (val instanceof NavigationEnd) {
        if (!this.router.getCurrentNavigation()?.extras?.state?.skipPageView) {
          this.analyticsService.pageView('MySE')
        }
      }
    }))
    this.spinnerService.start()
    this.loadSeBar()
    this.householdProfilesService.selfPersona.subscribe(persona => {
      this.persona = persona
      this.personaCropImage = (persona?.profile_images || []).find(i => i.image_type === 'crop')?.url
    })
    this.householdProfilesService.loadSelfPersona()
    this.path = this.window.location.pathname

    await Promise.all([
      this.themeLoadPromise,
      this.seFeTranslationsLoader.load(),
      firstValueFrom(this.dibSessionService.findAll()),
      firstValueFrom(this.userPreferenceService.mine()),
      firstValueFrom(hasOpenRegistrationEnabled(this.organizationService, this.siteMembershipService)),
    ]).then(result => {
      const [__theme, __t, dibs, userPreferences, showOpenRegistration] = result
      this.dibs = dibs
      this.showOrderHistory = userPreferences.find(p => p.key === 'enable_order_history')?.value === true
      this.showOpenRegistration = showOpenRegistration
      this.loaded = true
      this.spinnerService.stop()
      this.setTitle(this.routeConfigData?.title)
    }).finally(() => this.buildLeftNav())
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  public handleNavigate(e: SeFeLeftNavEvent): void {
    this.routerService.handleNavigate(e)
    this.layout.closeLeftNav()
  }

  private resetSeRems(): void {
    // resetting this becasuse something is calculating it before
    // all the styles are present and it ended up being too large
    document.documentElement.style.removeProperty('--se-rem')
    setSeRem()
  }

  private showHideNav(val: ActivationEnd): void {
    const hideParam = val.snapshot.queryParams?.hide_nav
    if (hideParam) {
      this.hideLeftNav = hideParam === '1'
      this.webViewService.setCurrent(this.hideLeftNav)
    }
    else {
      this.hideLeftNav = this.isWebView
    }
  }

  // The SeFeLeftNavRouterService (used in this app) will correctly select items matching the current url path.
  // If you add routes that break this pattern, you will need to add a `key` below, in the nav data AND a matching
  // data.leftNavKey property to the route config. The "Dashboard" item below is an example of this.
  private buildLeftNav(): void {
    this.navItems = [
      { icon: 'dashboard', label: this.translate.instant('DASHBOARD.title'), key: 'Dashboard', attributes: { href: '/user' } },
      { icon: 'family_group', label: this.translate.instant('USER.household'), attributes: { href: '/user/household' } },
      { icon: 'calendar_month', label: this.translate.instant('SCHEDULE.schedule'), attributes: { href: '/user/schedule' } },
      { icon: 'group', label: this.translate.instant('TEAMS.teams'), attributes: { href: '/user/my-teams' } },
    ]

    if (this.showOrderHistory) {
      this.navItems.push(
        { icon: 'receipt', label: this.translate.instant('ORDERS.title'), attributes: { href: '/user/order-history' } },
      )
    }

    const registrationsHref = this.showOpenRegistration
      ? '/user/registrations/open'
      : '/user/registrations'

    this.navItems.push(
      {
        icon: 'request_payment',
        label: this.translate.instant('USER.bills'),
        key: 'Bills',
        gaLabel: 'Bills',
        attributes: { href: '/user/bills' },
      },
      {
        icon: 'registration',
        label: this.translate.instant('REGISTRATIONS.registrations'),
        key: 'Registrations',
        gaLabel: 'Registrations',
        attributes: { href: registrationsHref }
      },
    )

    this.navItems.push(
      { icon: 'id_card', label: this.translate.instant('MEMBERSHIPS.title'), attributes: { href: '/user/memberships' } },
      { icon: 'badge', label: this.translate.instant('CREDENTIALS.title'), attributes: { href: '/user/credentials' } }
    )

    if (this.dibs.length) {
      this.navItems.push({
        icon: 'volunteer',
        label: this.translate.instant('DIBS.dibs'),
        attributes: { href: '/user/dibs' }
      })
    }

    this.navItems.push(
      { icon: 'video_camera_front', label: this.translate.instant('VIDEO.title'), attributes: { href: '/user/video_streaming' } },
    )

    this.footerLinks.push(
      { icon: 'settings', label: this.translate.instant('ACCOUNT.account_settings'), attributes: { href: '/user/account' } },
      {
        icon: 'question_circle_outline',
        label: 'Need Help',
        attributes: { href: 'https://help.sportsengine.com/en/collections/3502726-for-parents', target: 'blank' },
      }
    )

    this.routerService.setNavTree(this.navItems, this.footerLinks)
    if (this.routeConfigData) {
      this.setLeftNavSelected(this.routeConfigData)
    }
  }

  private setLeftNavSelected(data: any) {
    const match = data?.leftNavItem || data?.title

    this.navItems.forEach(nav => nav.isSelected = (match === nav.gaLabel))
    document.dispatchEvent(
      new CustomEvent('se-left-nav.triggerTreeChanges')
    )
  }

  private navigationContextData(event: ActivationEnd): Data {
    const data = event.snapshot.routeConfig?.data || {}
    const isOrderFromBills = event.snapshot.routeConfig?.path === 'orders/:orderId' &&
      event.snapshot.queryParams?.returnTo === '/user/bills'

    return isOrderFromBills
      ? { ...data, leftNavItem: 'Bills', leftNavKey: 'Bills' }
      : data
  }

  private setTitle(title: string): void {
    if (title) {
      this.titleService.setTitle(this.translate.instant('ROUTE_TITLE.page_title_template', { title }))
    }
    else {
      this.titleService.setTitle(this.translate.instant('ROUTE_TITLE.page_title_default'))
    }
  }

  private loadSeBar(): void {
    const seBarScript = document.createElement('script')
    seBarScript.src = environment.seBar
    document.head.appendChild(seBarScript)
  }
}
